/* eslint-disable @typescript-eslint/no-unused-vars */
import { useCallback, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useIntl } from "react-intl";
import { useQueryClient } from "@tanstack/react-query";
import { useStateMachine } from "little-state-machine";

import { useGetShipmentInvoices } from "api/queries/invoice/useGetShipmentInvoices";
import Button from "components/atoms/Button/Button";
import { useGetInvoiceConfig } from "api/queries/invoice/useGetInvoiceConfig";
import { updateBackgroundLoadingState } from "store/store";
import { createInvoiceDraft, deleteInvoiceDraft, revertRemovedInvoice } from "api";
import errorToast from "helpers/errorToast";
import { invoiceQueryKeys } from "api/queries/invoice/invoiceQueryKeys";
import useCustomBrokerPermission from "hooks/useCustomBrokerPermission";
import CancelInvoiceDialog from "../CancelInvoiceDialog/CancelInvoiceDialog";

function ShipmentBillingActions() {
  const intl = useIntl();
  const [isCreating, setIsCreating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isReverting, setIsReverting] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [isCustomBroker] = useCustomBrokerPermission();
  const { actions } = useStateMachine({ updateBackgroundLoadingState });
  const queryClient = useQueryClient();
  const { id: shipmentId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentInvoiceId = searchParams.get("invoice");
  const { data: invoices }: any = useGetShipmentInvoices({
    shipmentId: shipmentId!,
    options: {
      select: (data: any) => data.results,
      refetchOnMount: false,
    },
  });
  const { data: invoiceConfig }: any = useGetInvoiceConfig({
    shipmentId: shipmentId!,
    currentInvoiceId: currentInvoiceId!,
    options: { refetchOnMount: false },
  });

  const currentInvoice = useMemo(
    () => invoices?.find((invoice: any) => invoice.invoice_id === currentInvoiceId),
    [currentInvoiceId, invoices],
  );

  const isCustomsInvoice = currentInvoice?.invoice_type === "customs_broker_invoice";
  const totalInvoiceCount = invoices.length;
  const canRemoveInvoice = ["Unsubmitted", "Declined"].includes(currentInvoice?.invoice_status) && !isCustomsInvoice;

  const handleAddAdditionalInvoice = () => {
    setIsCreating(true);
    createInvoiceDraft(shipmentId)
      .then(() => {
        setIsCreating(false);
        actions.updateBackgroundLoadingState(true);
        queryClient.invalidateQueries(invoiceQueryKeys.shipmentInvoicesListing(shipmentId!));
      })
      .catch((err) => {
        setIsCreating(false);
        errorToast(err);
      });
  };

  const handleRevertRemoval = () => {
    setIsReverting(true);
    revertRemovedInvoice(currentInvoiceId)
      .then((res) => {
        setIsReverting(false);
        actions.updateBackgroundLoadingState(true);
        queryClient.invalidateQueries(invoiceQueryKeys.shipmentInvoicesListing(shipmentId!));
      })
      .catch((err) => {
        setIsReverting(false);
        errorToast(err);
      });
  };

  const handleRemoveInvoice = () => {
    setIsDeleting(true);
    deleteInvoiceDraft(currentInvoiceId)
      .then(() => {
        setIsDeleting(false);
        searchParams.delete("invoice");
        setSearchParams(searchParams);
        actions.updateBackgroundLoadingState(true);
        queryClient.invalidateQueries(invoiceQueryKeys.shipmentInvoicesListing(shipmentId!));
      })
      .catch((err) => {
        setIsDeleting(false);
        errorToast(err);
      });
  };

  const handleCancelAndUpdateInvoice = () => {
    setShowCancelModal(true);
  };

  const renderAddInvoiceButton = useCallback(
    () =>
      !isCustomsInvoice &&
      totalInvoiceCount - 1 < invoiceConfig?.additional_invoice_limit &&
      invoiceConfig?.invoice_audit_type !== "no_invoicing" ? (
        <Button
          variant="primary"
          className="rounded-pill blue-200 mr-2"
          onClick={handleAddAdditionalInvoice}
          label={intl.formatMessage({
            id: "billing.addAdditionalInvoice",
            defaultMessage: "Add Additional Invoice",
          })}
          isLoading={isCreating}
        />
      ) : null,
    [
      isCustomsInvoice,
      totalInvoiceCount,
      invoiceConfig?.additional_invoice_limit,
      isCreating,
      invoiceConfig?.invoice_audit_type,
    ],
  );

  const renderRevertRemovalButton = useCallback(
    () => (
      <Button
        variant="danger"
        className="rounded-pill"
        onClick={handleRevertRemoval}
        label={intl.formatMessage({
          id: "billing.revertRemoval",
          defaultMessage: "Revert Removal",
        })}
        isLoading={isReverting}
      />
    ),
    [isReverting, currentInvoiceId],
  );

  const renderCancelAndUpdateButton = () => (
    <>
      {renderAddInvoiceButton()}
      <Button
        variant="danger"
        className="rounded-pill"
        onClick={handleCancelAndUpdateInvoice}
        label={intl.formatMessage({
          id: "billing.cancelUpdate",
          defaultMessage: "Cancel & Update",
        })}
        isLoading={false}
      />
      <CancelInvoiceDialog
        isOpen={showCancelModal}
        close={() => setShowCancelModal(false)}
        currentInvoice={currentInvoice}
      />
    </>
  );

  const renderAdditionalButtons = () => {
    if (currentInvoice.invoice_status === "Removed") {
      return renderRevertRemovalButton();
    }

    return (
      <>
        {renderAddInvoiceButton()}
        {canRemoveInvoice && (
          <Button
            variant="danger"
            className="rounded-pill"
            onClick={handleRemoveInvoice}
            label={intl.formatMessage({
              id: "general.remove",
              defaultMessage: "Remove",
            })}
            isLoading={isDeleting}
          />
        )}
        {currentInvoice.invoice_status === "Auto Removed" &&
          invoiceConfig?.invoice_audit_type !== "no_invoicing" &&
          renderRevertRemovalButton()}
      </>
    );
  };

  return !isCustomBroker ? (
    <div className="d-flex align-items-center">
      {currentInvoice.invoice_status === "Awaiting Audit" ? renderCancelAndUpdateButton() : renderAdditionalButtons()}
    </div>
  ) : (
    <div>{currentInvoice.invoice_status === "Awaiting Audit" && renderCancelAndUpdateButton()}</div>
  );
}

export default ShipmentBillingActions;
